import { useId, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Check, ChevronRight } from "lucide-react";
import {
  LineKindBadge,
  LineKindMarker,
  POLineItemProductCell,
} from "@/components/po-line-item-product-cell";
import { POShipmentLine } from "@/components/po-shipment-line";
import { StatusPill } from "@/components/status-pill";
import { isActiveShipment } from "@/lib/shipments";
import {
  calculateProjectFinancials,
  type ProjectFinancialOrder,
  type ProjectOrderLine,
  type ProjectPurchaseOrder,
} from "@/components/project-po-utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatMoney,
  formatPercent,
  formatStatus,
} from "@/lib/utils";
import {
  POTimelineLine,
  type PurchaseOrderTimeline,
} from "@/components/delivery-timeline";
import { OrderStatusChips } from "@/components/order-status-chips";
import { PurchaseOrderActivity } from "@/components/entity-activity-badge";

function finiteNumber(value: string | number | null | undefined) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function ProjectFinancialMetrics({
  orders,
  additionalNet = 0,
  variant = "desktop",
}: {
  orders: ProjectFinancialOrder[];
  additionalNet?: number | null;
  variant?: "desktop" | "mobile";
}) {
  const financials = calculateProjectFinancials(orders, additionalNet);
  const metrics = [
    {
      label: "Revenue",
      value: financials.revenue == null ? "—" : formatMoney(financials.revenue),
    },
    {
      label: "Net",
      value: financials.net == null ? "—" : formatMoney(financials.net),
    },
    {
      label: "Profit",
      value: financials.profit == null ? "—" : formatMoney(financials.profit),
    },
    {
      label: "Margin",
      value: financials.margin == null ? "—" : formatPercent(financials.margin),
    },
  ];

  return (
    <div
      role="region"
      className={
        variant === "desktop"
          ? "hidden shrink-0 items-center gap-5 text-sm md:flex"
          : "grid grid-cols-2 gap-3 text-sm"
      }
      aria-label="Project financial summary"
    >
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className={variant === "desktop" ? "lg:text-right" : undefined}
        >
          <p className="text-xs text-muted-foreground">{metric.label}</p>
          <p className="font-semibold tabular-nums">{metric.value}</p>
        </div>
      ))}
    </div>
  );
}

function compareLines(a: ProjectOrderLine, b: ProjectOrderLine) {
  return (
    a.display_order - b.display_order ||
    a.item_number - b.item_number ||
    a.id - b.id
  );
}

function orderedLines(lines: ProjectOrderLine[]) {
  const lineIds = new Set(lines.map((line) => line.id));
  const children = new Map<number, ProjectOrderLine[]>();
  const roots: ProjectOrderLine[] = [];
  for (const line of lines) {
    if (line.parent_line_item_id && lineIds.has(line.parent_line_item_id)) {
      const group = children.get(line.parent_line_item_id) ?? [];
      group.push(line);
      children.set(line.parent_line_item_id, group);
    } else roots.push(line);
  }
  roots.sort(compareLines);
  children.forEach((group) => group.sort(compareLines));

  const result: ProjectOrderLine[] = [];
  const visited = new Set<number>();
  const addLine = (line: ProjectOrderLine) => {
    if (visited.has(line.id)) return;
    visited.add(line.id);
    result.push(line);
    children.get(line.id)?.forEach(addLine);
  };
  roots.forEach(addLine);
  lines
    .filter((line) => !visited.has(line.id))
    .sort(compareLines)
    .forEach(addLine);
  return result;
}

/** PO specs by model, so a sold line reads the same here as on its purchase order. */
type PoSpecs = Map<string, string>;

function poSpecs(po: ProjectPurchaseOrder | null): PoSpecs {
  const specs: PoSpecs = new Map();
  for (const line of po?.line_items ?? []) {
    if (line.description?.trim()) specs.set(line.model.trim().toLowerCase(), line.description.trim());
  }
  return specs;
}

/**
 * A sub-line's left gutter: its (i)/(W) kind marker, then the chevron. The gutter has a fixed
 * width so the chevron sits at the same indent whether or not the line has a marker.
 */
function SubLineGutter({ line, className }: { line: ProjectOrderLine; className?: string }) {
  return (
    <span
      data-sub-line-gutter="true"
      className={`flex w-8 shrink-0 items-center justify-end gap-1 ${className ?? ""}`}
    >
      <LineKindMarker kind={line.line_kind} />
      <ChevronRight
        className="h-3 w-3 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    </span>
  );
}

/** SKU first, then the PO's specs for it; the storefront title when the line is on no PO. */
function LineProduct({
  line,
  specs,
  compactImage,
}: {
  line: ProjectOrderLine;
  specs: PoSpecs;
  compactImage?: boolean;
}) {
  const poSpec = line.sku ? specs.get(line.sku.trim().toLowerCase()) : undefined;
  const description =
    poSpec ??
    ([line.sku ? line.title : null, line.description]
      .filter((part): part is string => Boolean(part && part.trim()))
      .join(" — ") ||
      null);
  return (
    <div className="min-w-0">
      <POLineItemProductCell
        model={line.sku || line.title}
        description={description}
        imageUrl={line.product?.image_url}
        showManufacturer={false}
        collapseLongDescription
        collapsedDescriptionLines={1}
        imageClassName={compactImage ? "h-9 w-9" : undefined}
      />
      <LineKindBadge
        kind={line.line_kind}
        label={formatStatus(line.line_kind)}
        className="mt-1"
      />
    </div>
  );
}

function ProjectOrderLineList({
  lines,
  po = null,
}: {
  lines: ProjectOrderLine[];
  po?: ProjectPurchaseOrder | null;
}) {
  const specs = poSpecs(po);
  const sortedLines = orderedLines(lines);
  return (
    <div className="@container/project-lines">
      <div
        data-project-order-lines-layout="table"
        className="hidden overflow-x-auto @2xl/project-lines:block"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="w-px text-right">Qty</TableHead>
              <TableHead className="w-px whitespace-nowrap text-right">Sell price</TableHead>
              <TableHead className="w-px whitespace-nowrap text-right">Sell total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLines.map((line) => {
              const child = Boolean(line.parent_line_item_id);
              return (
                <TableRow
                  key={line.id}
                  className={child ? "bg-muted/25" : undefined}
                >
                  <TableCell className="min-w-0 whitespace-normal">
                    <div
                      className={
                        child
                          ? "flex min-w-0 items-start gap-2"
                          : "min-w-0"
                      }
                    >
                      {child ? <SubLineGutter line={line} className="h-5" /> : null}
                      <LineProduct line={line} specs={specs} />
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {line.quantity}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {formatMoney(line.unit_price)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
                    {formatMoney(line.line_total)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div data-project-order-lines-layout="cards" className="divide-y @2xl/project-lines:hidden">
        {sortedLines.map((line) => {
          const child = Boolean(line.parent_line_item_id);
          return (
            <div
              key={line.id}
              className={`flex min-w-0 items-start gap-3 px-3 py-3 ${child ? "ml-5 bg-muted/25" : ""}`}
            >
              {child ? (
                <SubLineGutter line={line} className="mt-2 h-5" />
              ) : null}
              <div className="min-w-0 flex-1">
                <LineProduct line={line} specs={specs} compactImage />
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums">
                  {formatMoney(line.line_total)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                  {line.quantity} × {formatMoney(line.unit_price)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function groupSummary(lines: ProjectOrderLine[]) {
  return {
    lineCount: lines.length,
    quantity: lines.reduce((sum, line) => sum + line.quantity, 0),
    sellTotal: lines.reduce(
      (sum, line) => sum + (finiteNumber(line.line_total) ?? 0),
      0,
    ),
  };
}

export function ProjectPOGroup({
  po,
  lines,
  timeline,
  selected,
  onSelectedChange,
  details,
}: {
  po: ProjectPurchaseOrder;
  lines: ProjectOrderLine[];
  /** The PO's delivery timeline (USA-1329), when it has one. */
  timeline?: PurchaseOrderTimeline;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** What the row opens to instead of its order lines (a held PO's review, USA-1333). */
  details?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const detailsId = `project-po-details-${id.replace(/:/g, "")}`;
  const summary = groupSummary(lines);
  const manufacturerName =
    po.manufacturer?.short_name || po.manufacturer?.name || "Manufacturer";
  const shipments = (po.shipments ?? []).filter(isActiveShipment);
  const statusSummary = po.status_summary;
  const quotedShipping = finiteNumber(po.quoted_freight_cost);

  return (
    <section
      className="overflow-hidden rounded-lg border"
      data-testid={`project-po-${po.uuid}`}
    >
      <div className="flex min-w-0 items-stretch bg-muted/20">
        {/* A phone stacks select over expand in one 44px column, leaving the row for the PO number. */}
        <div className="flex w-11 shrink-0 flex-col border-r sm:contents">
          {onSelectedChange ? (
            <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center sm:h-auto sm:w-9 sm:border-r">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                aria-label={`Select purchase order ${po.po_number}`}
                checked={!!selected}
                onChange={(event) => onSelectedChange(event.target.checked)}
              />
            </label>
          ) : null}
          <button
            type="button"
            className="flex min-h-11 w-11 flex-1 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50 sm:min-h-0 sm:w-10 sm:flex-none"
            aria-expanded={expanded}
            aria-controls={detailsId}
            aria-label={`${expanded ? "Hide" : "Show"} items on purchase order ${po.po_number}`}
            onClick={() => setExpanded((current) => !current)}
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
              aria-hidden="true"
            />
          </button>
        </div>
        <Link
          to="/purchase-orders/$poId"
          params={{ poId: po.uuid }}
          aria-label={`Open purchase order ${po.po_number}`}
          className="flex min-w-0 flex-1 flex-col transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50"
        >
          {/* Columns that read across: PO number over manufacturer, its statuses over what is in
              flight for it, Sell over counts. A narrow row puts statuses and progress underneath. */}
          <span className="@container/po-head block py-2.5 pl-3 pr-3 sm:pl-0">
            <span className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 @lg/po-head:grid-cols-[10rem_minmax(0,1fr)_auto]">
              <span className="col-start-1 row-start-1 flex min-h-5 min-w-0 items-center">
                {/* Never cut off on a phone: Sell is the only thing beside it. */}
                <span className="min-w-0 text-sm font-semibold [overflow-wrap:anywhere] @lg/po-head:truncate">
                  {po.po_number}
                </span>
              </span>
              {/* Narrow: manufacturer and counts share a line of their own, so the counts never
                  squeeze the PO number. Wide: each takes its column. */}
              <span className="col-span-2 row-start-2 flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 @lg/po-head:contents">
                <span
                  className="min-w-0 truncate text-xs text-muted-foreground @lg/po-head:col-span-1 @lg/po-head:col-start-1 @lg/po-head:row-start-2"
                  title={manufacturerName}
                >
                  {manufacturerName}
                </span>
                <span className="ml-auto text-right text-xs text-muted-foreground tabular-nums @lg/po-head:col-start-3 @lg/po-head:row-start-2 @lg/po-head:ml-0">
                  {summary.lineCount} {summary.lineCount === 1 ? "line" : "lines"} · {summary.quantity} qty
                  {quotedShipping != null && <> · Shipping {formatMoney(quotedShipping)}</>}
                </span>
              </span>
              <span className="col-span-2 row-start-3 flex min-h-5 min-w-0 flex-wrap items-center gap-1.5 empty:hidden @lg/po-head:col-span-1 @lg/po-head:col-start-2 @lg/po-head:row-start-1">
                {po.po_review === "reviewed" ? (
                  <StatusPill tone="success">
                    <Check className="mr-1 size-3" aria-hidden="true" />
                    Reviewed
                  </StatusPill>
                ) : po.po_review === "needs_review" ? (
                  <StatusPill tone="warning">Needs review</StatusPill>
                ) : statusSummary ? (
                  <OrderStatusChips
                    compact
                    order={statusSummary.order}
                    shipping={statusSummary.shipping}
                    ap={statusSummary.ap}
                  />
                ) : null}
              </span>
              <span className="col-span-2 row-start-4 min-w-0 empty:hidden @lg/po-head:col-span-1 @lg/po-head:col-start-2 @lg/po-head:row-start-2">
                <PurchaseOrderActivity
                  purchaseOrderId={po.id}
                  shipmentIds={(po.shipments ?? []).map((shipment) => shipment.id)}
                />
              </span>
              <span className="col-start-2 row-start-1 flex min-h-5 items-center justify-end whitespace-nowrap text-sm font-semibold tabular-nums @lg/po-head:col-start-3">
                {/* A phone row needs the room for the PO number. */}
                <span className="mr-1 hidden text-xs font-normal text-muted-foreground @lg/po-head:inline">Sell{" "}</span>
                {formatMoney(summary.sellTotal)}
              </span>
            </span>
          </span>
          {(timeline && timeline.status !== "delivered") ||
          shipments.length > 0 ||
          statusSummary?.attention?.length ? (
            // One shared pill column: timeline, shipment and item rows line up under each other.
            <span className="grid grid-cols-1 gap-x-3 gap-y-1.5 border-t bg-background py-2 pl-3 pr-3 sm:grid-cols-[9.5rem_minmax(0,1fr)] sm:pl-0">
              {timeline ? <POTimelineLine timeline={timeline} /> : null}
              {shipments.map((shipment) => (
                <POShipmentLine key={shipment.id} shipment={shipment} aligned />
              ))}
              {statusSummary?.attention?.map((item) => (
                <span
                  key={item.line_item_id}
                  className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:col-span-2 sm:grid sm:grid-cols-subgrid sm:justify-items-start"
                >
                  <StatusPill tone={item.tone}>{item.label}</StatusPill>
                  <span className="flex min-w-0 sm:w-full items-center gap-2.5">
                    <span className="shrink-0 font-medium">{item.model}</span>
                    {item.description ? (
                      <span className="min-w-0 truncate text-muted-foreground">{item.description}</span>
                    ) : null}
                  </span>
                </span>
              ))}
            </span>
          ) : null}
        </Link>
      </div>

      {expanded ? (
        <div id={detailsId} className="border-t bg-background">
          {details ?? <ProjectOrderLineList lines={lines} po={po} />}
        </div>
      ) : null}
    </section>
  );
}

export function ProjectManualGroup({ lines }: { lines: ProjectOrderLine[] }) {
  const id = useId();
  const titleId = `project-manual-lines-${id.replace(/:/g, "")}`;
  if (lines.length === 0) return null;
  const summary = groupSummary(lines);
  return (
    <section
      className="overflow-hidden rounded-lg border"
      aria-labelledby={titleId}
    >
      <div className="flex min-w-0 flex-col gap-1 bg-muted/20 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 id={titleId} className="text-sm font-semibold">
            Manual
          </h3>
          <p className="text-xs text-muted-foreground">
            Sold lines excluded from automatically cut purchase orders
          </p>
        </div>
        <div className="shrink-0 text-sm tabular-nums sm:text-right">
          <span className="text-xs text-muted-foreground">
            {summary.lineCount} {summary.lineCount === 1 ? "line" : "lines"} ·{" "}
            {summary.quantity} qty
          </span>
          <span className="ml-2 font-semibold">
            Sell {formatMoney(summary.sellTotal)}
          </span>
        </div>
      </div>
      <div className="border-t">
        <ProjectOrderLineList lines={lines} />
      </div>
    </section>
  );
}

export function ProjectUnassignedGroup({
  lines,
}: {
  lines: ProjectOrderLine[];
}) {
  const id = useId();
  const titleId = `project-unassigned-lines-${id.replace(/:/g, "")}`;
  if (lines.length === 0) return null;
  return (
    <section
      className="overflow-hidden rounded-lg border"
      aria-labelledby={titleId}
    >
      <div className="bg-muted/20 px-3 py-2 text-sm font-semibold" id={titleId}>
        Unassigned order lines
      </div>
      <div className="border-t">
        <ProjectOrderLineList lines={lines} />
      </div>
    </section>
  );
}
